// SMS segment + cost math (US A2P 10DLC pricing baseline)
// Twilio charges per SEGMENT, not per message. A single message that uses non-GSM
// characters (emoji, curly quotes, etc.) switches to UCS-2 and shrinks segment
// capacity from 160 -> 70 chars, doubling or tripling the bill silently.

// Full GSM-7 basic + extension table
const GSM_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "^{}\\[~]|€";

// Cost per segment (base + avg carrier surcharge, US A2P 10DLC as of 2026)
export const COST_PER_SEGMENT_USD = 0.013;

export function isGsm7(text) {
  if (!text) return true;
  for (const ch of text) {
    if (!GSM_BASIC.includes(ch) && !GSM_EXT.includes(ch)) return false;
  }
  return true;
}

/**
 * Return an object describing SMS segmentation + cost for a given body.
 * { chars, segments, encoding: 'GSM-7'|'UCS-2', maxPerSegment, costUsd, willSplit }
 */
export function analyzeSms(body) {
  const text = body || "";
  const gsm = isGsm7(text);
  // In GSM-7, extension chars count as 2 chars
  let effectiveLen = 0;
  if (gsm) {
    for (const ch of text) {
      effectiveLen += GSM_EXT.includes(ch) ? 2 : 1;
    }
  } else {
    effectiveLen = [...text].length; // count code points, close enough for UCS-2 estimate
  }
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67; // concatenated SMS uses UDH so 7 chars reserved per segment
  let segments;
  if (effectiveLen === 0) segments = 0;
  else if (effectiveLen <= single) segments = 1;
  else segments = Math.ceil(effectiveLen / multi);
  return {
    chars: text.length,
    effectiveChars: effectiveLen,
    encoding: gsm ? "GSM-7" : "UCS-2",
    segments,
    maxPerSegment: segments <= 1 ? single : multi,
    remaining: Math.max(0, (segments <= 1 ? single : multi) - effectiveLen % (segments <= 1 ? single : multi)) % (segments <= 1 ? single : multi),
    costUsd: segments * COST_PER_SEGMENT_USD,
    willSplit: segments > 1,
    hasNonAscii: !gsm,
  };
}

// Replace common non-GSM chars with GSM equivalents (curly quotes, em dashes, etc.)
// Leaves emojis alone — you'll still hit UCS-2 if you use them.
export function toAsciiSafe(text) {
  if (!text) return text;
  return text
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/\u2013|\u2014/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/\u00A0/g, " ")
    .replace(/\u2022/g, "*");
}

export function formatUsd(n) {
  if (n < 1) return `${(n * 100).toFixed(1)}¢`;
  return `$${n.toFixed(2)}`;
}
